import Vue from 'vue';
import Vuex from 'vuex';

import axios, { AxiosResponse } from 'axios';
import _ from 'lodash';
import moment from 'moment-timezone';
import 'leaflet/dist/leaflet.css';
import { Icon } from 'leaflet';

// Workaround for missing map markers from https://vue2-leaflet.netlify.app/quickstart/#marker-icons-are-missing
type D = Icon.Default & {
  _getIconUrl?: string;
};
delete (Icon.Default.prototype as D)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const BASE_URL = process.env.API_BASE_URL ?? 'http://localhost:8000/';

const client = axios.create({
  baseURL: BASE_URL,
  headers: {
    Authorization: `Bearer ${window.localStorage.getItem('jwtAccess')}`,
  },
});

Vue.use(Vuex);

export interface Credentials {
  username: string;
  password: string;
}

interface JWTResponse {
  access: string;
  refresh: string;
  display_name: string;
}

interface JWTRefreshResponse {
  access: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export type TransactionType = 'C' | 'D' | '3';

interface Incident {
  readonly incident_type: number;
  readonly by: number;
  readonly by_name: string;
  readonly at: string;
}

interface BaseConsignment {
  readonly tracking_id: number;
  readonly consignment_number: string;
  readonly customer_reference: string;
  readonly transaction: TransactionType;
  readonly account_code: string;
  readonly account_name: string;
  readonly date_entered: string;
  readonly time_entered: string;
  readonly due_date: string;
  readonly due_time: string;
  readonly consignment_status: string;
  readonly delivery_depot_number: number;
  // Extra data
  readonly original_due_date: string;
  readonly original_due_time: string;
  readonly delivery_address_contact: string;
  readonly delivery_address_company: string;
  readonly delivery_address_country: string;
  readonly delivery_address_postcode: string;
  readonly incident: Incident | null;
  readonly incident_type_url: string;
  readonly at_risk: boolean;
  readonly review_note_url: string;
  readonly review_note: string;
  readonly eta: string;
  readonly lat: number;
  readonly lng: number;
  readonly excluded_from_review: boolean;
  readonly exclude_url: string;
}

interface UnreviewedConsignment extends BaseConsignment {
  readonly reviewed: false;
  readonly review_url: string;
}

interface ReviewedConsignment extends BaseConsignment {
  readonly reviewed: true;
  readonly unreview_url: string;
  readonly reviewed_by: string;
  readonly reviewed_at: string;
}

export type Consignment = UnreviewedConsignment | ReviewedConsignment;

export interface ConsignmentStatus {
  consignment_status_id: number;
  consignment_status: string;
}

export interface Account {
  account_code: string;
  account_name: string;
}

export interface AccountWithID extends Account {
  account_id: number;
}

export interface Depot {
  depot_number: number;
  depot_name: string;
}

export interface IncidentType {
  id: number;
  name: string;
  is_obsolete: boolean;
}

export interface AMSGroup {
  id: number;
  name: string;
}

export interface ConsignmentFilters {
  status?: number[];
  transaction: TransactionType[];
  account: string[];
  hmDepot: number[];
  deliveryDepot: number[];
  enteredFrom?: moment.Moment;
  enteredTo?: moment.Moment;
  dueFrom?: moment.Moment;
  dueTo?: moment.Moment;
  amsGroup: number[];
}

export type JobType = 'Own' | 'Network' | 'Network Collection';

interface FilterSpec {
  jobType: JobType;
  generic?: string;
  ordering?: string[];
  filters?: ConsignmentFilters;
}

interface ReviewResponse {
  reviewed_by: string;
  reviewed_at: string;
  unreview_url: string;
}

interface UnreviewResponse {
  review_url: string;
}

enum AUTH_FAILURE_TYPE {
  UNAUTHORISED,
  FORBIDDEN,
}

export interface CustomerReportSchedule {
  id: number | undefined;
  customer_name: string;
  morning: boolean;
  midday: boolean;
  eod: boolean;
  include_monthly_progress: boolean;
  accounts: number[];
  email_recipients: {name: string; email: string}[];
}

function momentToParam(m?: moment.Moment) {
  if (m?.isValid()) {
    return m.format('YYYY-MM-DD');
  } else {
    return undefined;
  }
}

function filterSpecToGetParams(filters?: FilterSpec) {
  return {
    jobType: filters?.jobType ?? 'Own',
    status: filters?.filters?.status,
    transaction: filters?.filters?.transaction,
    account: filters?.filters?.account,
    hmDepot: filters?.filters?.hmDepot,
    deliveryDepot: filters?.filters?.deliveryDepot,
    amsGroup: filters?.filters?.amsGroup,
    enteredFrom: momentToParam(filters?.filters?.enteredFrom),
    enteredTo: momentToParam(filters?.filters?.enteredTo),
    dueFrom: momentToParam(filters?.filters?.dueFrom),
    dueTo: momentToParam(filters?.filters?.dueTo),
    search: filters?.generic,
    ordering: filters?.ordering?.join(','),
  };
}

const store = new Vuex.Store({
  state: {
    jwtAccess: window.localStorage.getItem('jwtAccess'),
    jwtRefresh: window.localStorage.getItem('jwtRefresh'),
    userDisplayName: window.localStorage.getItem('userDisplayName'),
    authFailureHandler: (() => { /* Do nothing by default */
    }) as (state: AUTH_FAILURE_TYPE) => void,
    jwtRefreshPromise: null as Promise<AxiosResponse<JWTRefreshResponse>> | null,
  },
  mutations: {
    setJwt(state, response: JWTResponse) {
      state.jwtAccess = response.access;
      state.jwtRefresh = response.refresh;
      state.userDisplayName = response.display_name;
      client.defaults.headers.Authorization = `Bearer ${response.access}`;
      window.localStorage.setItem('jwtAccess', response.access);
      window.localStorage.setItem('jwtRefresh', response.refresh);
      window.localStorage.setItem('userDisplayName', response.display_name);
    },
    clearJwt(state) {
      state.jwtAccess = null;
      state.jwtRefresh = null;
      delete client.defaults.headers.Authorization;
      window.localStorage.removeItem('jwtAccess');
      window.localStorage.removeItem('jwtRefresh');
      window.localStorage.removeItem('userDisplayName');
    },
    setJwtRefreshPromise(state, promise: Promise<AxiosResponse<JWTRefreshResponse>>) {
      state.jwtRefreshPromise = promise;
    },
    refreshJwt(state, accessToken: string) {
      state.jwtAccess = accessToken;
      client.defaults.headers.Authorization = `Bearer ${accessToken}`;
      state.jwtRefreshPromise = null;
    },
    setAuthFailureHandler(state, handler: (state: AUTH_FAILURE_TYPE) => void) {
      state.authFailureHandler = handler;
    },
  },
  getters: {
    isLoggedIn(state) {
      return state.jwtAccess !== null;
    },
    username(state): string | null {
      if (state.jwtAccess === null) {
        return null;
      } else {
        return state.userDisplayName;
      }
    },
    showExperimentalFeatures(state, getters): boolean {
      return getters.username !== null && ['Peter Jackson', 'David Bywater'].includes(getters.username);
    },
  },
  actions: {
    async login(context, credentials: Credentials) {
      const response = await client.post<JWTResponse>('aaa/token/', credentials);
      context.commit('setJwt', response.data);
    },
    logout(context) {
      context.commit('clearJwt');
    },
    async refreshJWT(context) {
      if (context.state.jwtRefreshPromise === null) {
        const promise = client.post<JWTRefreshResponse>('aaa/token/refresh/', {
          refresh: context.state.jwtRefresh,
        });
        context.commit('setJwtRefreshPromise', promise);
        context.commit('refreshJwt', (await promise).data.access);
      } else {
        await context.state.jwtRefreshPromise;
      }
    },
  },
  modules: {
    consignments: {
      namespaced: true,
      state: {
        loading: true,
        consignments: [] as Consignment[],
        nextLink: null as string | null,
        prevLink: null as string | null,
        total: 0,
      },
      getters: {
        hasNext(state) {
          return state.nextLink !== null;
        },
        hasPrev(state) {
          return state.prevLink !== null;
        },
        total(state) {
          return state.total;
        },
        currentPage(state) {
          return state.consignments;
        },
      },
      mutations: {
        _startLoad(state) {
          state.loading = true;
        },
        _update(state, response: PaginatedResponse<Consignment>) {
          state.consignments = response.results;
          state.nextLink = response.next;
          state.prevLink = response.previous;
          state.total = response.count;
          state.loading = false;
        },
        _updateConsignment(state, consignment: Consignment) {
          const idx = state.consignments.findIndex((c: Consignment) => c.tracking_id === consignment.tracking_id);
          state.consignments.splice(idx, 1, consignment);
        },
      },
      actions: {
        load: _.debounce(async function(context, filters?: FilterSpec) {
          context.commit('_startLoad');

          const params: { [key: string]: string | string[] | number[] | undefined } = filterSpecToGetParams(filters);
          const response = await client.get<PaginatedResponse<Consignment>>('api/consignment/', {
            params,
          });
          context.commit('_update', response.data);
        }, 1000),
        async prev(context) {
          context.commit('_startLoad');
          const response = await client.get<PaginatedResponse<Consignment>>(context.state.prevLink);
          context.commit('_update', response.data);
        },
        async next(context) {
          context.commit('_startLoad');
          const response = await client.get<PaginatedResponse<Consignment>>(context.state.nextLink);
          context.commit('_update', response.data);
        },
        async markReviewed(context, consignment: UnreviewedConsignment) {
          const response = await client.post<ReviewResponse>(consignment.review_url);
          context.commit('_updateConsignment', {
            ...consignment,
            ...response.data,
            reviewed: true,
          });
        },
        async markUnreviewed(context, consignment: ReviewedConsignment) {
          const response = await client.post<UnreviewResponse>(consignment.unreview_url);
          context.commit('_updateConsignment', {
            ...consignment,
            ...response.data,
            reviewed: false,
            // eslint-disable-next-line @typescript-eslint/camelcase
            review_note: '',
          });
        },
        async excludeFromReview(context, consignment: Consignment) {
          await client.post(consignment.exclude_url);
          context.commit('_updateConsignment', {
            ...consignment,
            // eslint-disable-next-line @typescript-eslint/camelcase
            excluded_from_review: true,
          });
        },
        async includeInReview(context, consignment: Consignment) {
          await client.delete(consignment.exclude_url);
          context.commit('_updateConsignment', {
            ...consignment,
            // eslint-disable-next-line @typescript-eslint/camelcase
            excluded_from_review: false,
          });
        },
        async setReviewNote(context, { consignment, note }: { consignment: BaseConsignment; note: string }) {
          const response = await client.put<ReviewResponse>(consignment.review_note_url, { note });
          context.commit('_updateConsignment', {
            ...consignment,
            ...response.data,
            // eslint-disable-next-line @typescript-eslint/camelcase
            review_note: note,
            reviewed: true,
          });
        },
        async recordIncident(context, { consignment, incidentTypeId }: {
          consignment: BaseConsignment;
          incidentTypeId: number;
        }) {
          const response = await client.post<BaseConsignment>(
            consignment.incident_type_url,
            { incidentTypeId },
          );
          context.commit('_updateConsignment', {
            ...consignment,
            ...response.data,
          });
        },
        async deleteIncident(context, consignment: BaseConsignment) {
          const response = await client.delete<BaseConsignment>(consignment.incident_type_url);
          context.commit('_updateConsignment', {
            ...consignment,
            ...response.data,
          });
        },
        async exportToExcel(context, filters?: FilterSpec): Promise<Blob> {
          const response = await client.get('api/consignment/excel/', {
            params: filterSpecToGetParams(filters),
            responseType: 'blob',
            headers: {
              accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            },
          });
          console.log(response.data);
          return response.data;
        },
      },
    },
    consignmentStatuses: {
      namespaced: true,
      state: {
        consignmentStatuses: [] as ConsignmentStatus[],
      },
      getters: {
        all(state) {
          return state.consignmentStatuses;
        },
      },
      mutations: {
        _set(state, statuses: ConsignmentStatus[]) {
          state.consignmentStatuses = statuses;
        },
      },
      actions: {
        async load(context) {
          const response = await client.get<ConsignmentStatus[]>('api/consignment-status/');
          context.commit('_set', response.data);
        },
      },
    },
    accounts: {
      namespaced: true,
      state: {
        accounts: [] as Account[],
      },
      getters: {
        all(state) {
          return state.accounts;
        },
      },
      mutations: {
        _set(state, accounts: Account[]) {
          state.accounts = accounts;
        },
      },
      actions: {
        async load(context) {
          const response = await client.get<Account[]>('api/account/');
          context.commit('_set', response.data);
        },
        async live(): Promise<AccountWithID[]> {
          const response = await client.get<AccountWithID[]>('api/account/live/');
          return response.data;
        },
        async includeNonLive(): Promise<AccountWithID[]> {
          const response = await client.get<AccountWithID[]>('api/account/include_non_live/');
          return response.data;
        },
      },
    },
    depots: {
      namespaced: true,
      state: {
        depots: [] as Depot[],
      },
      getters: {
        all(state) {
          return state.depots;
        },
      },
      mutations: {
        _set(state, depots: Depot[]) {
          state.depots = depots;
        },
      },
      actions: {
        async load(context) {
          const response = await client.get<Depot[]>('api/depot/');
          context.commit('_set', response.data);
        },
      },
    },
    incidentTypes: {
      namespaced: true,
      state: {
        incidentTypes: [] as IncidentType[],
      },
      getters: {
        all(state) {
          return state.incidentTypes;
        },
      },
      mutations: {
        _set(state, incidentTypes: IncidentType[]) {
          incidentTypes.sort((a, b) => {
            if (a.is_obsolete === b.is_obsolete) {
              return 0;
            }
            if (a.is_obsolete) {
              return 1;
            }
            if (b.is_obsolete) {
              return -1;
            }
            return 0;
          });
          state.incidentTypes = incidentTypes;
        },
      },
      actions: {
        async load(context) {
          const response = await client.get<IncidentType[]>('api/incident-type/');
          context.commit('_set', response.data);
        },
      },
    },
    amsGroups: {
      namespaced: true,
      state: {
        amsGroups: [] as AMSGroup[],
      },
      getters: {
        all(state) {
          return state.amsGroups;
        },
      },
      mutations: {
        _set(state, amsGroups: AMSGroup[]) {
          state.amsGroups = amsGroups;
        },
      },
      actions: {
        async load(context) {
          const response = await client.get<AMSGroup[]>('ams/account-group/');
          context.commit('_set', response.data);
        },
      },
    },
    customerReportSchedules: {
      namespaced: true,
      actions: {
        async list(): Promise<CustomerReportSchedule[]> {
          const response = await client.get<CustomerReportSchedule[]>('api/customer-report-schedule/');
          return response.data;
        },
        async save(context, schedule: CustomerReportSchedule) {
          if (schedule.id) {
            await client.put(`api/customer-report-schedule/${schedule.id}/`, schedule);
          } else {
            const response = await client.post<CustomerReportSchedule>('api/customer-report-schedule/', schedule);
            schedule.id = response.data.id;
          }
        },
        async update(context, schedule: CustomerReportSchedule) {
          await client.post(`api/customer-report-schedule/${schedule.id}/update_report/`);
        },
        async download(context, schedule: CustomerReportSchedule): Promise<AxiosResponse<Blob>> {
          return await client.get(`api/customer-report-schedule/${schedule.id}/download/`, {
            responseType: 'blob',
          });
        },
      },
    },
  },
});

const defaultAdapter = client.defaults.adapter;

client.defaults.adapter = async(config) => {
  if (defaultAdapter === undefined) {
    throw new Error('No default adapter configured for Axios');
  }
  try {
    return await defaultAdapter(config);
  } catch (e) {
    if (config.url === 'aaa/token/refresh/') {
      return e.response;
    }
    switch (e.response.status) {
      case 401:
        try {
          await store.dispatch('refreshJWT');
          config.headers.Authorization = `Bearer ${store.state.jwtAccess}`;
          return await defaultAdapter(config);
        } catch {
          store.commit('clearJwt');
          store.state.authFailureHandler(AUTH_FAILURE_TYPE.UNAUTHORISED);
          return e.response;
        }
      case 403:
        store.commit('clearJwt');
        store.state.authFailureHandler(AUTH_FAILURE_TYPE.FORBIDDEN);
        return e.response;
      default:
        return e.response;
    }
  }
};

export default store;
